import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { delelteObtainContract, getContractDetail } from '../../api/contract';
import { formatDateTime } from '../../utils/util';
import { AlertModal } from '../../components/modal/alert.modal';
import { ConfirmModal } from '../../components/modal/confirm.modal';
import {
  fetchArrivalNotification,
  fetchSendNotificationDriver,
} from '../../api/alert';

interface ContractDetailResponse {
  obtainId: number;
  orderId: number;
  name: string;
  wanted: string;
  targetDate: string;
  targetTime: string;
  work: string;
  createdAt: string;
  obtainStatus: string;
}

interface FetchSendNotificationResponse {
  orderId: number;
  obtainId: number;
  vehicle?: string;
  name: string;
  content: string;
  createdAt: string;
}

const ContractDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<ContractDetailResponse>();
  const [isArrivalButtonDisabled, setIsArrivalButtonDisabled] = useState(false);
  const [arrivalNotiList, setArrivalNotiList] = useState<
    FetchSendNotificationResponse[]
  >([]);

  // 모달
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적
  const [confirmModal, setConfirmModal] = useState(false);

  useEffect(() => {
    const getfetchData = async () => {
      try {
        const response = await getContractDetail(Number(id));
        setData(response);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };

    getfetchData();
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/userContract',
      });
    }
  };

  const handleConfirmClose = () => {
    setConfirmModal(false);
  };

  // 현재 날짜와 도착일, 시간을 비교하여 알림 버튼 표시 여부를 결정하는 로직 추가
  const isAfterToday = (dateString: string, timeString: string) => {
    const currentDateTime = new Date(); // 현재 날짜와 시간
    // targetTime을 HH시 MM분 형식에서 HH:MM 형식으로 변환
    const formattedTimeString = timeString
      .replace(/시|분/g, '')
      .trim()
      .replace(/\s+/g, ':');

    // targetDate와 targetTime을 결합하여 Date 객체 생성
    const targetDateTime = new Date(`${dateString}T${formattedTimeString}:00`);

    return currentDateTime < targetDateTime; // 현재 시간이 targetDateTime 이전인지 확인
  };

  // "수주 취소" 버튼 활성화 여부를 결정하는 함수
  const isBeforeTargetDate = (dateString: string) => {
    const today = new Date().toISOString().substring(0, 10);
    const date = new Date(dateString).toISOString().substring(0, 10);

    //date.setDate(date.getDate() - 1); // targetDate의 전날을 계산
    //date.toISOString().substring(0, 10);

    return today < date; // 오늘이 전날보다 작거나 같은지 확인, 전일까지만 수주 취소 가능하니 작은지만 확인
  };

  const handleDeleteContract = async () => {
    try {
      const response = await delelteObtainContract(Number(id));
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('수주 내역 삭제에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error) {
      setModalMessage('수주 내역 삭제에 실패했습니다.');
      setIsModalOpen(true);
    }
  };

  const arrivalClickedHandler = async (orderId: number, obtainId: number) => {
    try {
      const response = await fetchArrivalNotification(orderId, obtainId);
      console.log(response);
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setIsArrivalButtonDisabled(true);
      } else {
        setModalMessage('도착 알림 전송에 실패했습니다.');
        setIsModalOpen(true);
      }
    } catch (error) {
      setModalMessage('도착 알림 전송에 실패했습니다.');
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    const getSendArrivalNotificationList = async () => {
      const response = await fetchSendNotificationDriver({});
      setArrivalNotiList(response);

      // arrivalNotiList를 검사하여 버튼 비활성화 상태 설정
      if (
        response.some(
          (noti) =>
            noti.orderId === data?.orderId && noti.obtainId === data?.obtainId,
        )
      ) {
        setIsArrivalButtonDisabled(true);
      }
    };

    getSendArrivalNotificationList();
  }, [data]);

  if (!data) return null; // 데이터가 없으면 아무것도 렌더링하지 않음

  return (
    <Container isAfterToday={isAfterToday(data.targetDate, data.targetTime)}>
      <ul>
        <li>
          <div className="label">회사명</div>
          <div className="value">{data?.name}</div>
        </li>
        <li>
          <div className="label">수배 차량</div>
          <div className="value">{data?.wanted}</div>
        </li>
        <li>
          <div className="label">도착 일자</div>
          <div className="value">{data?.targetDate}</div>
        </li>
        <li>
          <div className="label">도착 시간</div>
          <div className="value">{data?.targetTime}</div>
        </li>
        <li>
          <div className="label">작업 형태</div>
          <div className="value">{data?.work === 'full' ? '전일' : '반일'}</div>
        </li>
        <li>
          <div className="label">수주 시간</div>
          <div className="value">
            {data?.createdAt
              ? formatDateTime(data.createdAt.toString())
              : 'N/A'}
          </div>
        </li>
        <li>
          <div className="label">수주 상태</div>
          <div className="value status">
            {data?.obtainStatus === 'Waiting' ? '신청' : '승인 완료'}
          </div>
        </li>
        <li>
          {isAfterToday(data.targetDate, data.targetTime) && (
            <Button
              onClick={() => {
                arrivalClickedHandler(data.orderId, data.obtainId);
              }}
              disabled={isArrivalButtonDisabled}
            >
              도착 알림
            </Button>
          )}
        </li>
      </ul>
      {/* "수주 취소" 버튼: targetDate 전날까지만 활성화 */}
      {isBeforeTargetDate(data.targetDate) ? (
        <CancleBtn onClick={handleDeleteContract}>수주 취소</CancleBtn>
      ) : (
        <CancleBtn disabled>수주 취소</CancleBtn>
      )}

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}

      {confirmModal ? (
        <ConfirmModal
          handleConfirmClose={handleConfirmClose}
          confirmText="해당 건을 수주 하시겠습니까?"
          onClick={handleDeleteContract}
        />
      ) : null}
    </Container>
  );
};

export default ContractDetail;

// style
const Container = styled.div<{ isAfterToday: boolean }>`
  padding: 0.6rem 1.5rem;
  ul {
    /* padding: 2.4rem 2.4rem 8.5rem; */
    padding: ${(props) =>
      props.isAfterToday ? '2.4rem 2.4rem 8.5rem' : '2.4rem'};
    border-radius: 1.6rem;
    box-shadow: 0 2px 5px 3px rgba(0, 0, 0, 0.05);
    position: relative;
    background-color: ${(props) => props.theme.textWhiteColor};
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    li:not(:last-child) {
      display: flex;
      gap: 2.4rem;
      align-items: center;
      .label {
        min-width: 5.4rem;
        font-size: 1.2rem;
        font-weight: 600;
        color: ${(props) => props.theme.textAAAColor};
      }
      .value {
        font-size: 1.4rem;
        font-weight: 600;
        &.status {
          min-width: 4.2rem;
          color: ${(props) => props.theme.textWhiteColor};
          background-color: ${(props) => props.theme.btnBlueColor};
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.4rem;
          font-size: 1.1rem;
          border-radius: 1.1rem;
        }
      }
    }
    li:last-child {
      position: absolute;
      bottom: 0;
      left: 0;
      padding: 0.7rem;
      width: 100%;
    }
  }
`;

const Button = styled.button<{ disabled?: boolean }>`
  width: 100%;
  /* background-color: ${(props) => props.theme.btnBlueColor}; */
  background-color: ${(props) =>
    props.disabled ? props.theme.gray700 : props.theme.btnBlueColor};
  color: ${(props) => props.theme.textWhiteColor};
  padding: 1.7rem;
  border-radius: 1.2rem;
  border: 0;
  outline: none;
  font-size: 1.8rem;
  font-weight: 600;
`;

const CancleBtn = styled.button<{ disabled?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 9.4rem;
  border: none;
  outline: none;
  padding: 1.7rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  /* background-color: ${(props) => props.theme.errorColor}; */
  background-color: ${(props) =>
    props.disabled ? props.theme.gray700 : props.theme.errorColor};
  color: ${(props) => props.theme.textWhiteColor};
  font-size: 1.8rem;
  font-weight: 600;
`;
